import { Info, Truck, FileText, Building2, Wifi } from 'lucide-react';
import { INSTALLED_BY, sek } from '@/lib/quoteCalc';

export const Section = ({ title, icon: Icon, children, testId }) => (
  <section className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-7" data-testid={testId}>
    <h2 className="flex items-center gap-2 text-base sm:text-lg font-bold text-slate-900 mb-4">
      {Icon && <Icon className="h-4 w-4 text-[#0f172a]" />}{title}
    </h2>
    {children}
  </section>
);

const Field = ({ label, value }) => !value ? null : (
  <div className="flex flex-col"><span className="text-[11px] uppercase tracking-wider text-slate-400">{label}</span><span className="text-sm text-slate-800 whitespace-pre-line break-words">{value}</span></div>
);

const d10 = (v) => (v ? String(v).slice(0, 10) : '');

export function PartiesSection({ q }) {
  const c = q.company || {};
  return (
    <Section title="Offertuppgifter" icon={Building2} testId="quote-parties">
      <div className="grid gap-6 sm:grid-cols-3">
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-x-4 gap-y-2.5 content-start">
          <p className="col-span-2 sm:col-span-1 text-xs font-semibold text-slate-900">Kunduppgifter</p>
          <Field label="Kund" value={q.school_name} />
          <Field label="Organisationsnummer" value={q.org_number} />
          <Field label="Kontaktperson" value={q.contact_name} />
          <Field label="E-post" value={q.contact_email} />
          <Field label="Er referens" value={q.reference} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-x-4 gap-y-2.5 content-start">
          <p className="col-span-2 sm:col-span-1 text-xs font-semibold text-slate-900">Offert</p>
          <Field label="Offertnummer" value={q.quote_number} />
          <Field label="Offertdatum" value={d10(q.created_at)} />
          <Field label="Giltig till och med" value={d10(q.valid_until)} />
          <Field label="Avtalstid" value={`${q.contract_months || 12} månader`} />
          <Field label="Betalningsvillkor" value={q.payment_terms || '30 dagar netto'} />
          <Field label="Fakturareferens" value={q.invoice_reference} />
          <Field label="Fakturaadress" value={q.invoice_address} />
          <Field label="Inköpsordernummer" value={q.po_number} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-x-4 gap-y-2.5 content-start">
          <p className="col-span-2 sm:col-span-1 text-xs font-semibold text-slate-900">Leverantör</p>
          <Field label="Företag" value={c.name} />
          <Field label="Organisationsnummer" value={c.org_number} />
          <Field label="Adress" value={c.address} />
          <Field label="Kontakt" value={[c.email, c.phone].filter(Boolean).join(' · ')} />
        </div>
      </div>
    </Section>
  );
}

function ItemTable({ items, title, accent }) {
  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className={`${accent} text-white text-xs font-semibold tracking-wide uppercase px-4 py-2.5`}>{title}</div>
      {items.map((it, i) => (
        <div key={i} className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-4 py-3 text-sm ${i % 2 ? 'bg-white' : 'bg-slate-50'}`}>
          <span className="flex-1 text-slate-800">{it.description}</span>
          <span className="text-slate-500 text-xs sm:text-sm sm:w-40 sm:text-right">{it.quantity} {it.unit} × {sek(it.unit_price)}</span>
          <span className="font-semibold text-slate-900 sm:w-28 text-right">{sek(it.total)}</span>
        </div>
      ))}
    </div>
  );
}

export function PriceSection({ q }) {
  const items = q.items || [];
  const by = (t) => items.filter(i => (i.item_type || 'hardware') === t);
  const dt = q.display_totals || {};
  const disc = q.discount_amount || 0;
  return (
    <Section title="Prisöversikt" icon={FileText} testId="quote-prices">
      <div className="space-y-3">
        <ItemTable items={by('hardware')} title="Hårdvara – engångskostnad" accent="bg-[#0f172a]" />
        <ItemTable items={by('accessory')} title="Tillbehör – engångskostnad" accent="bg-[#0f172a]" />
        <ItemTable items={by('license')} title="Vapelarm Cloud – licens, service & support" accent="bg-blue-700" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        <div className="rounded-xl border border-slate-200 p-4">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">Engångskostnad</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1" data-testid="quote-onetime">{sek(dt.one_time_total)}</p>
          <p className="text-xs text-slate-500 mt-0.5">Hårdvara, tillbehör{q.installation_cost ? ' och installation' : ''}, exkl. moms{disc > 0 ? ` (efter rabatt ${q.discount_percent}%)` : ''}</p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
          <p className="text-[11px] uppercase tracking-wider text-blue-700">Återkommande licenskostnad</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1" data-testid="quote-recurring">{sek(dt.recurring_annual)}<span className="text-sm font-semibold text-slate-500">/år</span></p>
          <p className="text-xs text-slate-500 mt-0.5">Vapelarm Cloud, service och support, exkl. moms</p>
        </div>
      </div>
      <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 sm:p-5 space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Hårdvara</span><span>{sek(q.hardware_total)}</span></div>
        {disc > 0 && <div className="flex justify-between text-red-600"><span>Rabatt hårdvara ({q.discount_percent}%)</span><span>-{sek(disc)}</span></div>}
        {q.accessory_total > 0 && <div className="flex justify-between"><span className="text-slate-500">Tillbehör</span><span>{sek(q.accessory_total)}</span></div>}
        {q.installation_cost > 0 && <div className="flex justify-between"><span className="text-slate-500">Installation</span><span>{sek(q.installation_cost)}</span></div>}
        <div className="flex justify-between"><span className="text-slate-500">Vapelarm Cloud – licens första året</span><span>{sek(q.license_total)}</span></div>
        <div className="flex justify-between pt-2 mt-1 border-t border-slate-200 font-semibold"><span>Totalt exklusive moms</span><span data-testid="quote-sum-ex">{sek(dt.sum_ex_vat)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Moms 25 %</span><span>{sek(dt.vat_amount)}</span></div>
      </div>
      <div className="mt-3 rounded-xl bg-[#0f172a] text-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1">
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-300">Totalt första avtalsåret</p>
          <p className="text-sm text-slate-300">Totalt inklusive moms</p>
        </div>
        <p className="text-3xl font-extrabold tracking-tight" data-testid="quote-sum-inkl">{sek(dt.sum_inkl_vat)}</p>
      </div>
      <div className="mt-4 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-slate-700" data-testid="quote-license-info">
        <Info className="h-4 w-4 text-blue-700 shrink-0 mt-0.5" />
        <div className="space-y-1.5">
          <p>{q.license_info_text}</p>
          <p className="font-semibold text-slate-900">Återkommande licenskostnad efter första året: {sek(dt.recurring_annual)}/år exkl. moms.</p>
          {q.renewal_text && <p className="text-slate-600">{q.renewal_text}</p>}
        </div>
      </div>
    </Section>
  );
}

export function DeliverySection({ q }) {
  const inc = q.installation_included;
  const has = q.delivery_time || inc !== undefined && inc !== null || q.installation_by || q.technical_requirements || q.wifi_required || q.delivery_comments;
  if (!has) return null;
  return (
    <Section title="Leverans och installation" icon={Truck} testId="quote-delivery">
      {inc === false && <p className="mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3 font-medium" data-testid="quote-install-not-included">Installation ingår inte i denna offert.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Beräknad leveranstid" value={q.delivery_time} />
        <Field label="Installation ingår" value={inc === true ? 'Ja' : inc === false ? 'Nej' : ''} />
        {inc === true && <Field label="Installationskostnad" value={q.installation_cost ? `${sek(q.installation_cost)} exkl. moms` : 'Ingår'} />}
        <Field label="Installation utförs av" value={INSTALLED_BY[q.installation_by]} />
        <Field label="Tekniska förutsättningar" value={q.technical_requirements} />
        <Field label="Övriga kommentarer" value={q.delivery_comments} />
      </div>
      {q.wifi_required && (
        <div className="mt-4 flex gap-2 text-sm text-slate-700 rounded-lg border border-slate-200 p-3" data-testid="quote-wifi-req">
          <Wifi className="h-4 w-4 text-[#0f172a] mt-0.5 shrink-0" />
          <span>WiFi-sensorerna kräver ett tillgängligt och kompatibelt trådlöst nätverk.{q.wifi_requirements ? ` ${q.wifi_requirements}` : ''}</span>
        </div>
      )}
    </Section>
  );
}
